import { defineComponent as _defineComponent } from 'vue'
import { ref, reactive, computed, onMounted } from 'vue'
import {
  useAddUserMutation,
  useAddVentureAndUserMutation,
  useCheckEmailDomainAccountWarningQuery,
  Module,
  AccountMembershipRole,
} from '~/types/types'
import { getTermsUrl } from '~/functions/accounts'
import { normalizeMembershipRoleKey } from '~/functions/membership-role'
import { useNuxtApp, useRoute, useRouter } from '#app'


export default /*#__PURE__*/_defineComponent({
  __name: 'RegisterComponent',
  setup(__props) {

const route = useRoute()
const router = useRouter()
const { firstName, lastName, email, companyUrl, companyName } = route.query
const register = reactive({
  firstName: firstName || '',
  lastName: lastName || '',
  email: (email || '').toString().trim(),
  password: '',
  confirmPassword: '',
  ventureName: companyName || '',
  logo: null as null | string,
  website: companyUrl || '',
})

const showThankYouMessage = ref(false)
const form = ref()
const { $axios, $i18n } = useNuxtApp()

const tokenStatus = ref({
  isEsgTokenValid: false,
  isMetricsTokenValid: false,
  isImpactGoalTokenValid: false,
  isImpactStrategyValid: false,
  isImpactTokenValid: false,
})
const tokenValid = ref(false)
const teamTokenExpired = ref(false)

const termsUrl = computed(() => getTermsUrl())
const portfolioCheckbox = ref(false)
const impactCheckbox = ref(false)

const logoUpload = async (file: File) => {
  if (!file) {
    // user removed the logo
    return
  }
  try {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('account_id', String(route.query.portfolio || ''))
    if (route.query.team) {
      formData.append('token', String(route.query.team))
    }
    if (route.query.esgToken) {
      formData.append('esg_token', String(route.query.esgToken))
    }
    if (route.query.metricsToken) {
      formData.append('metrics_token', String(route.query.metricsToken))
    }
    if (route.query.impactGoalToken) {
      formData.append('impact_goal_token', String(route.query.impactGoalToken))
    }
    const response = await $axios.post('/api/upload/registration', formData, {
      headers: {
        'content-Type': 'multipart/form-data',
      },
    })
    if (response.data?.[0]) {
      register.logo = response.data[0]
      window.$nuxt.$emit('userError', {
        message: $i18n.t('companyPerspective.registrationPage.uploadedSuccessfully'),
        color: 'green',
      })
    }
  } catch (err: any) {
    window.$nuxt.$emit('userError', { ...err, color: 'red' })
    console.log(err)
  }
}

const loading = ref(true)
const data = ref()
const isJwtExpired = (token: unknown): boolean => {
  if (typeof token !== 'string') return false

  const parts = token.split('.')
  if (parts.length !== 3) return false

  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const payloadJson = atob(base64)
    const payload = JSON.parse(payloadJson) as { exp?: number }

    if (!payload.exp || typeof payload.exp !== 'number') return false

    const nowInSeconds = Math.floor(Date.now() / 1000)
    return payload.exp < nowInSeconds
  } catch {
    return false
  }
}

const isTrueQueryParam = (value: unknown): boolean =>
  typeof value === 'string' && value.toLowerCase() === 'true'

const isCompanyInfoQueryParam = (value: unknown): boolean =>
  value === undefined || value === null || value === ''
    ? true
    : typeof value === 'string' && value.toLowerCase() === 'true'

const isTeamMemberRegistration = computed(() => !!route.query.team)
const hasAcceptedPortfolioSharing = computed(
  () => !!data.value?.hasAcceptedPortfolioSharing,
)
const showPortfolioSharingCheckbox = computed(
  () =>
    !isTeamMemberRegistration.value || !hasAcceptedPortfolioSharing.value,
)
const isPortfolioSignupFlow = computed(
  () => !!route.query.portfolio && !isTeamMemberRegistration.value,
)
const normalizedRegisterEmail = computed(() => register.email.trim().toLowerCase())
const hasValidRegisterEmail = computed(() =>
  /.+@.+\..+/.test(normalizedRegisterEmail.value),
)

const domainWarningVariables = computed(() => ({
  email: normalizedRegisterEmail.value,
}))
const { result: domainWarningResult, refetch: refetchDomainWarning } =
  useCheckEmailDomainAccountWarningQuery(
    domainWarningVariables,
    computed(() => ({
      enabled: isPortfolioSignupFlow.value && hasValidRegisterEmail.value,
    })),
  )
const domainWarning = computed(
  () => domainWarningResult.value?.checkEmailDomainAccountWarning,
)
const hasDomainAccountWarning = computed(
    () => Boolean(domainWarning.value?.domainMatched),
  )
const showDomainWarningDialog = ref(false)
const isConfirmingDomainWarning = ref(false)
const submitAfterDomainWarningConfirm = ref(false)
const showRegistrationFailedDialog = ref(false)
const registrationFailedMessage = ref(
  'Registration failed. If you already have an account with us, please check your email.',
)

const modules = computed(() => {
  if (data?.value?.accountId === '61f34375-3129-c125-9afa-597b4ec40584') {
    // very simple customization
    return [
      {
        key: 'isEsgTokenValid',
        name: 'ESG',
      },
      {
        key: 'isImpactGoalTokenValid',
        name: 'GHG & Impact Estimator​',
      },
    ]
      .filter(({ key }) => data?.value?.[key])
      .map(({ name }) => name)
  }
  return [
    {
      key: 'isEsgTokenValid',
      name: $i18n.t('companyPerspective.registrationPage.esgModule'),
    },
    {
      key: 'isMetricsTokenValid',
      name: $i18n.t('companyPerspective.registrationPage.kpiModule'),
    },
    {
      key: 'isImpactGoalTokenValid',
      name: $i18n.t('companyPerspective.registrationPage.impactGoalModule'),
    },
    {
      key: 'isImpactStrategyValid',
      name: $i18n.t('companyPerspective.registrationPage.impactStrategyModule'),
    },
    {
      key: 'isImpactTokenValid',
      name: $i18n.t('companyPerspective.registrationPage.impactModule'),
    },
    {
      key: 'isCompanyInfoEnabled',
      name: $i18n.t('companyPerspective.registrationPage.companyInfoModule'),
    },
  ]
    .filter(({ key }) => data?.value?.[key])
    .map(({ name }) => name)
})

onMounted(async () => {
  try {
    if (route.query.portfolio) {
      // this is for when register for a new company
      const response = await $axios.post('/api/auth/verifyurl', {
        recordId: route.query.portfolio,
        esgToken: route.query.esgToken,
        metricsToken: route.query.metricsToken,
        impactGoalToken: route.query.impactGoalToken,
        impactStrategy: isTrueQueryParam(route.query.impactStrategy),
        impactToken: route.query.impactToken,
        companyInfo: isCompanyInfoQueryParam(route.query.companyInfo),
      })
      data.value = response.data || {}

      tokenStatus.value = {
        isEsgTokenValid: data.value.isEsgTokenValid,
        isMetricsTokenValid: data.value.isMetricsTokenValid,
        isImpactGoalTokenValid: data.value.isImpactGoalTokenValid,
        isImpactStrategyValid: data.value.isImpactStrategyValid,
        isImpactTokenValid: data.value.isImpactTokenValid,
      }
      tokenValid.value =
        tokenStatus.value.isEsgTokenValid ||
        tokenStatus.value.isMetricsTokenValid ||
        tokenStatus.value.isImpactGoalTokenValid ||
        tokenStatus.value.isImpactStrategyValid ||
        tokenStatus.value.isImpactTokenValid
    } else if (route.query.team) {
      teamTokenExpired.value = isJwtExpired(route.query.token)

      const response = await $axios.post('/api/auth/verifyurl', {
        recordId: route.query.team,
        token: route.query.token,
      })
      console.log('response.data', response.data)
      data.value = response.data || {}
      tokenValid.value =
        !!response.data.isVentureTokenValid && !teamTokenExpired.value
    }
    loading.value = false
  } catch (err) {
    console.error(err)
  }
})

const selfRegister = computed(() => data?.value?.accountId === '61f34375-3129-c125-9afa-597b4ec40584')
const company = computed(() => data?.value?.ventureName)

const teamInviteRole = computed(() => {
  const role = data.value?.teamInviteMembershipRole
  if (role === AccountMembershipRole.Reviewer || role === 'REVIEWER') {
    return AccountMembershipRole.Reviewer
  }
  if (
    role === AccountMembershipRole.Editor ||
    role === 'EDITOR' ||
    data.value?.isVentureTokenValid
  ) {
    return AccountMembershipRole.Editor
  }
  return undefined
})

const teamInviteMessageKey = computed(() => {
  if (normalizeMembershipRoleKey(teamInviteRole.value) === 'REVIEWER') {
    return 'companyPerspective.registrationPage.teamHasInvitedYouToReview'
  }
  return 'companyPerspective.registrationPage.teamHasInvitedYouToCollaborate'
})

const consenteeName = computed(() => {
  const names = data?.value?.portfolioNames
  if (Array.isArray(names) && names.length > 0) {
    return names.join(', ')
  }
  return company.value
})

const {
  mutate: addUserMutate,
  onDone: onAddUserDone,
  onError: onAddUserError,
  loading: addUserLoading,
} = useAddUserMutation()
const {
  mutate: addVentureMutate,
  onDone: onAddVentureDone,
  loading: addVentureAndUserLoading,
} = useAddVentureAndUserMutation()

onAddUserError((error: any) => {
  const message = error?.graphQLErrors?.[0]?.message || error?.message || ''
  if (message.includes('Registration failed.')) {
    registrationFailedMessage.value = message
    showRegistrationFailedDialog.value = true
  }
})

const onSubmit = async () => {
  if (isTeamMemberRegistration.value) {
    if (form.value.validate()) {
      const variables: any = {
        firstName: register.firstName,
        lastName: register.lastName,
        password: register.password,
        email: register.email.trim(),
        accountId: route.query.team,
        token: route.query.token,
        hasAcceptedPortfolioSharing: portfolioCheckbox.value,
      }
      addUserMutate(variables)
      onAddUserDone(({ data }) => {
        if (data?.addUser?.email) {
          showThankYouMessage.value = true
        }
      })
    }
  } else if (form.value.validate()) {
    if (isPortfolioSignupFlow.value) {
      try {
        isConfirmingDomainWarning.value = true
        const response = await refetchDomainWarning({
          email: normalizedRegisterEmail.value,
        })
        const warning = response.data?.checkEmailDomainAccountWarning
        const hasWarning = Boolean(warning?.domainMatched)
        if (hasWarning && !submitAfterDomainWarningConfirm.value) {
          showDomainWarningDialog.value = true
          return
        }
      } finally {
        isConfirmingDomainWarning.value = false
      }
    }
    submitAfterDomainWarningConfirm.value = false
    const variables: any = {
      firstName: register.firstName,
      lastName: register.lastName,
      password: register.password,
      email: register.email.trim(),
      ventureName: register.ventureName,
      esgToken: route.query.esgToken,
      metricsToken: route.query.metricsToken,
      impactGoalToken: route.query.impactGoalToken,
      impactStrategy: isTrueQueryParam(route.query.impactStrategy),
      hasAcceptedPortfolioSharing: portfolioCheckbox.value,
      companyInfo: isCompanyInfoQueryParam(route.query.companyInfo),
      accountId: route.query.portfolio,
      logo: register.logo,
      website: register.website,
    }
    addVentureMutate(variables)
    onAddVentureDone(({ data }) => {
      if (data?.addVentureAndUser?.email) {
        showThankYouMessage.value = true
        accountRefetch()
      }
    })
  }
}

const cancelDomainWarningSubmit = () => {
  showDomainWarningDialog.value = false
  submitAfterDomainWarningConfirm.value = false
}

const continueDomainWarningSubmit = () => {
  submitAfterDomainWarningConfirm.value = true
  showDomainWarningDialog.value = false
  onSubmit()
}

const closeRegistrationFailedDialog = () => {
  register.firstName = ''
  register.lastName = ''
  register.email = ''
  register.password = ''
  register.confirmPassword = ''
  showRegistrationFailedDialog.value = false
}

const goToLoginFromRegistrationFailure = () => {
  showRegistrationFailedDialog.value = false
  router.push('/login')
}


return { __sfc: true,route, router, firstName, lastName, email, companyUrl, companyName, register, showThankYouMessage, form, $axios, $i18n, tokenStatus, tokenValid, teamTokenExpired, termsUrl, portfolioCheckbox, impactCheckbox, logoUpload, loading, data, isJwtExpired, isTrueQueryParam, isCompanyInfoQueryParam, isTeamMemberRegistration, hasAcceptedPortfolioSharing, showPortfolioSharingCheckbox, isPortfolioSignupFlow, normalizedRegisterEmail, hasValidRegisterEmail, domainWarningVariables, domainWarningResult, refetchDomainWarning, domainWarning, hasDomainAccountWarning, showDomainWarningDialog, isConfirmingDomainWarning, submitAfterDomainWarningConfirm, showRegistrationFailedDialog, registrationFailedMessage, modules, selfRegister, company, teamInviteRole, teamInviteMessageKey, consenteeName, addUserMutate, onAddUserDone, onAddUserError, addUserLoading, addVentureMutate, onAddVentureDone, addVentureAndUserLoading, onSubmit, cancelDomainWarningSubmit, continueDomainWarningSubmit, closeRegistrationFailedDialog, goToLoginFromRegistrationFailure }
}

})